import type { GroupRef, PersonDetail, Role } from '@ralph/contracts'
import { ArrowLeft, Ban, KeyRound, LogOut, RotateCcw, ShieldCheck, UsersRound } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { GroupAvatar } from '@/components/groups/GroupAvatar.tsx'
import { RemoveManagerDialog, RemoveMemberDialog } from '@/components/groups/GroupDialogs.tsx'
import {
  AddToGroupDialog,
  DeactivateDialog,
  GrantDialog,
  ReactivateDialog,
  RemoveRoleDialog
} from '@/components/people/AccessDialogs.tsx'
import { historyChange, historySentence } from '@/components/people/people-plane.ts'
import { RoleDetailRow } from '@/components/people/RoleChips.tsx'
import { Notice, SectionHead } from '@/components/ui/admin-chrome.tsx'
import { Badge, Button, Panel, Spinner } from '@/components/ui/primitives.tsx'
import { useApproveGrant, useRevokeSessions } from '@/hooks/use-people.ts'
import { useI18n } from '@/i18n/index.ts'

/**
 * One person: what they may do, where it came from, and what they are accountable for.
 *
 * The order is the order of an administrator's questions. *May do* is first because it is why
 * anybody opened this panel; *Groups* is second because being in a group is not a label but the
 * use of everything shared with it, and the counts beside each group say so; *Accountable for* is
 * third because it is what turns "deactivate them" from a click into a decision; sessions and
 * history are last, because they answer questions people only ask afterwards.
 *
 * Groups decide what a person can *use*, roles what they may *do* (PRD-26 D-24), which is why the
 * two sections sit apart and neither mentions the other.
 *
 * Identity is at the top and has no controls at all. It belongs to the directory, and a surface
 * that let an administrator retype a colleague's name would be a surface that could impersonate
 * one.
 */

type DialogState =
  | { kind: 'grant'; effect: 'allow' | 'deny'; role?: Role }
  | { kind: 'remove'; role: Role; effect: 'allow' | 'deny' }
  | { kind: 'deactivate' }
  | { kind: 'reactivate' }
  | { kind: 'addToGroup' }
  | { kind: 'leaveGroup'; group: GroupRef }
  | { kind: 'stopManaging'; group: GroupRef }
  | null

export const PersonPanel = ({
  person,
  isLoading,
  canWrite,
  onBack
}: {
  person: PersonDetail | undefined
  isLoading: boolean
  canWrite: boolean
  onBack?: () => void
}) => {
  const fmt = useI18n()
  const t = fmt.t.people
  const [dialog, setDialog] = useState<DialogState>(null)
  const [signedOut, setSignedOut] = useState<number | null>(null)
  const revokeSessions = useRevokeSessions()
  const approve = useApproveGrant()

  if (isLoading) {
    return (
      <p className="flex items-center gap-2 px-5 py-6 text-[13px] text-fg-subtle">
        <Spinner /> {t.list.loading}
      </p>
    )
  }

  if (!person) {
    return (
      <div className="px-5 py-10 text-center">
        <p className="text-[14px] font-semibold">{t.person.pick}</p>
        <p className="mt-1 text-[13px] text-fg-muted">{t.person.pickBody}</p>
      </div>
    )
  }

  const deactivated = person.status === 'deactivated'

  return (
    <div className="fade-in space-y-5 px-5 py-5 sm:px-6">
      {/* Only where the detail replaced the list. On `lg` the list is still on screen, and a
          back control that goes nowhere visible is a control that teaches people to distrust
          the others. */}
      {onBack && (
        <div className="lg:hidden">
          <Button size="sm" variant="ghost" icon={<ArrowLeft size={14} aria-hidden />} onClick={onBack}>
            {t.title}
          </Button>
        </div>
      )}

      {/* ---- identity ---- */}
      <header>
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
          <h2 className="text-[22px] tracking-[-0.02em]">{person.displayName}</h2>
          {person.isYou && <Badge tone="primary">{t.list.you}</Badge>}
          {deactivated && <Badge tone="alert">{t.list.deactivatedBadge}</Badge>}
        </div>
        <p className="mt-0.5 text-[13px] text-fg-muted">{person.email}</p>
        <p className="readout mt-1.5 text-fg-subtle">
          {person.providerSlug} · {t.person.firstSeenLine(fmt.formatAgo(person.createdAt))} ·{' '}
          {person.lastLoginAt ? t.person.lastSeenLine(fmt.formatAgo(person.lastLoginAt)) : t.list.never}
        </p>
        {deactivated && (
          <p className="mt-2 text-[12.5px] text-fg-muted">
            {t.person.deactivatedBy(person.deactivatedByName, fmt.formatAgo(person.deactivatedAt ?? ''))}
            {person.deactivationReason !== '' && ` · ${t.person.deactivatedReason(person.deactivationReason)}`}
          </p>
        )}
        {/* The server refuses a self-demotion either way; not offering the control is what stops
            an administrator from discovering that through a 412 they did not expect. */}
        {person.isYou && canWrite && <p className="mt-2 text-[12.5px] text-fg-subtle">{t.person.selfNote}</p>}
      </header>

      {/* ---- may do ---- */}
      <section>
        <SectionHead
          title={t.person.mayDo}
          lead={t.person.mayDoLead}
          action={
            canWrite && !deactivated ? (
              <Button
                size="sm"
                icon={<KeyRound size={13} aria-hidden />}
                onClick={() => setDialog({ kind: 'grant', effect: 'allow' })}
              >
                {t.person.grant}
              </Button>
            ) : undefined
          }
        />

        <Panel className="mt-3 px-4 py-1">
          {person.roles.map((assignment) => (
            <RoleDetailRow
              key={`${assignment.role}-${assignment.provenance}-${assignment.grantId ?? 'none'}`}
              assignment={assignment}
              action={
                canWrite && !(person.isYou && assignment.role === 'admin') ? (
                  <>
                    {assignment.provenance === 'grant' && assignment.pendingApproval && !person.isYou && (
                      <Button
                        size="sm"
                        variant="primary"
                        disabled={approve.isPending}
                        onClick={() =>
                          assignment.grantId && approve.mutate({ userId: person.id, grantId: assignment.grantId })
                        }
                      >
                        {t.person.approve}
                      </Button>
                    )}
                    {assignment.provenance === 'grant' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDialog({ kind: 'remove', role: assignment.role, effect: 'allow' })}
                      >
                        {t.person.remove}
                      </Button>
                    )}
                    {/* A role the directory sends cannot be removed here — it can be overridden,
                        and the word says which of the two this is. `configuration` gets no control
                        at all: its home is AUTH_PROVIDERS_JSON, and the sentence says so. */}
                    {assignment.provenance === 'directory' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDialog({ kind: 'grant', effect: 'deny', role: assignment.role })}
                      >
                        {t.person.removeHere}
                      </Button>
                    )}
                  </>
                ) : undefined
              }
            />
          ))}
        </Panel>

        {person.denials.length > 0 && (
          <div className="mt-3 space-y-2">
            {person.denials.map((denial) => (
              <Notice
                key={denial.role}
                title={`${t.person.denied}: ${t.roleLabel[denial.role]}`}
                icon={<Ban size={12} aria-hidden />}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span>
                    {denial.stillSent ? t.person.deniedStillSent(denial.role) : t.person.deniedNoLongerSent}
                    {denial.reason !== '' && ` ${t.provenance.reason(denial.reason)}`}
                  </span>
                  {canWrite && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setDialog({ kind: 'remove', role: denial.role, effect: 'deny' })}
                    >
                      {t.person.lift}
                    </Button>
                  )}
                </div>
              </Notice>
            ))}
          </div>
        )}
      </section>

      {/* ---- groups ---- */}
      <section>
        <SectionHead
          title={t.person.isIn}
          lead={t.person.isInLead}
          action={
            canWrite && !deactivated ? (
              <Button
                size="sm"
                icon={<UsersRound size={13} aria-hidden />}
                onClick={() => setDialog({ kind: 'addToGroup' })}
              >
                {t.person.addToGroup}
              </Button>
            ) : undefined
          }
        />
        {person.groupDetail.length === 0 ? (
          <p className="mt-2 text-[13px] text-fg-subtle">{t.person.noGroups}</p>
        ) : (
          <ul className="mt-3 space-y-2">
            {person.groupDetail.map((entry) => (
              <li key={entry.group.id}>
                <Panel className="flex flex-wrap items-start gap-x-3 gap-y-2 px-4 py-2.5">
                  <GroupAvatar group={entry.group} size={28} className="mt-0.5" />
                  <div className="min-w-0 flex-1 basis-[12rem]">
                    <p className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[13.5px] font-semibold">
                      <Link to={`/groups/${entry.group.id}`} className="min-w-0 break-words hover:underline">
                        {entry.group.name}
                      </Link>
                      <span className="readout font-normal text-fg-subtle">
                        {entry.isMember && entry.isManager
                          ? t.person.groupRelation.both
                          : entry.isManager
                            ? t.person.groupRelation.manager
                            : t.person.groupRelation.member}{' '}
                        · {t.person.groupMembers(fmt, entry.group.memberCount)}
                      </span>
                    </p>
                    <p className="mt-0.5 text-[12.5px] text-fg-muted">
                      {entry.isMember
                        ? t.person.groupUnlocks(fmt, entry.knowledgeBaseCount, entry.agentCount)
                        : t.person.managesOnly}
                    </p>
                  </div>
                  {canWrite && (
                    <div className="flex shrink-0 flex-wrap items-center gap-1">
                      {entry.isManager && (
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-label={t.person.stopManagingLabel(entry.group.name)}
                          onClick={() => setDialog({ kind: 'stopManaging', group: entry.group })}
                        >
                          {t.person.stopManaging}
                        </Button>
                      )}
                      {entry.isMember && (
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-label={t.person.removeFromGroup(entry.group.name)}
                          onClick={() => setDialog({ kind: 'leaveGroup', group: entry.group })}
                        >
                          {t.person.removeShort}
                        </Button>
                      )}
                    </div>
                  )}
                </Panel>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ---- accountable for ---- */}
      <section>
        <SectionHead title={t.person.accountable} lead={t.person.accountableLead} />
        {person.owns.agents.length === 0 &&
        person.owns.knowledgeBases.length === 0 &&
        person.owns.conversationCount === 0 ? (
          <p className="mt-2 text-[13px] text-fg-subtle">{t.person.ownsNothing}</p>
        ) : (
          <Panel className="mt-3 space-y-1.5 px-4 py-3 text-[13px]">
            {person.owns.agents.length > 0 && (
              <p>
                {t.person.agents(fmt, person.owns.agents.length)} —{' '}
                {person.owns.agents.map((agent, index) => (
                  <span key={agent.agentId}>
                    {index > 0 && ', '}
                    <Link to={`/agents/${agent.agentId}`} className="hover:underline">
                      {agent.displayName}
                    </Link>
                  </span>
                ))}
              </p>
            )}
            {person.owns.knowledgeBases.length > 0 && (
              <p>
                {t.person.bases(fmt, person.owns.knowledgeBases.length)} —{' '}
                {person.owns.knowledgeBases.map((base, index) => (
                  <span key={base.id}>
                    {index > 0 && ', '}
                    <Link to={`/knowledge/${base.id}`} className="hover:underline">
                      {base.name}
                    </Link>
                  </span>
                ))}
              </p>
            )}
            {person.owns.conversationCount > 0 && <p>{t.person.conversations(fmt, person.owns.conversationCount)}</p>}
          </Panel>
        )}
      </section>

      {/* ---- sessions ---- */}
      <section>
        <SectionHead
          title={t.person.sessions}
          lead={t.person.sessionsLead}
          action={
            canWrite && person.sessions.length > 0 ? (
              <Button
                size="sm"
                variant="ghost"
                icon={<LogOut size={13} aria-hidden />}
                disabled={revokeSessions.isPending}
                onClick={() =>
                  revokeSessions.mutate({ userId: person.id }, { onSuccess: (result) => setSignedOut(result.revoked) })
                }
              >
                {t.person.signOutEverywhere}
              </Button>
            ) : undefined
          }
        />
        {signedOut !== null && (
          <p role="status" className="mt-2 text-[12.5px] text-fg-muted">
            {t.person.signedOut(fmt, signedOut)}
          </p>
        )}
        {person.sessions.length === 0 ? (
          <p className="mt-2 text-[13px] text-fg-subtle">{t.person.noSessions}</p>
        ) : (
          <ul className="mt-3 space-y-1.5">
            {person.sessions.map((session) => (
              <li key={session.id} className="readout flex flex-wrap items-center gap-x-2 text-fg-subtle">
                <span>{fmt.formatAgo(session.lastSeenAt)}</span>
                {session.ipAddress && <span>· {session.ipAddress}</span>}
                {session.current && <span className="text-fg-muted">· {t.person.thisDevice}</span>}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ---- history ---- */}
      <section>
        <SectionHead title={t.person.history} lead="" />
        {person.history.length === 0 ? (
          <p className="mt-2 text-[13px] text-fg-subtle">{t.person.noHistory}</p>
        ) : (
          <ul className="mt-3 space-y-1.5">
            {person.history.map((entry) => {
              const before = entry.detail.before
              const after = entry.detail.after
              const change =
                Array.isArray(before) && Array.isArray(after)
                  ? historyChange(t, before as string[], after as string[])
                  : null

              return (
                <li key={entry.id} className="text-[12.5px] leading-[1.5]">
                  <span className="text-fg-muted">{historySentence(t, entry.action, entry.detail)}</span>{' '}
                  <span className="text-fg-subtle">
                    · {entry.actorLabel} · {fmt.formatAgo(entry.at)}
                  </span>
                  {change && <span className="readout ml-1 text-fg-subtle">{change}</span>}
                </li>
              )
            })}
          </ul>
        )}
      </section>

      {/* ---- lifecycle ---- */}
      {canWrite && !person.isYou && (
        <section className="border-t border-rule pt-4">
          {deactivated ? (
            <Button
              variant="secondary"
              icon={<RotateCcw size={14} aria-hidden />}
              onClick={() => setDialog({ kind: 'reactivate' })}
            >
              {t.person.reactivate}
            </Button>
          ) : (
            <Button
              variant="danger"
              icon={<ShieldCheck size={14} aria-hidden />}
              onClick={() => setDialog({ kind: 'deactivate' })}
            >
              {t.person.deactivate}
            </Button>
          )}
        </section>
      )}

      {dialog?.kind === 'grant' && (
        <GrantDialog
          person={person}
          effect={dialog.effect}
          {...(dialog.role ? { presetRole: dialog.role } : {})}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'remove' && (
        <RemoveRoleDialog person={person} role={dialog.role} effect={dialog.effect} onClose={() => setDialog(null)} />
      )}
      {dialog?.kind === 'deactivate' && <DeactivateDialog person={person} onClose={() => setDialog(null)} />}
      {dialog?.kind === 'reactivate' && <ReactivateDialog person={person} onClose={() => setDialog(null)} />}
      {dialog?.kind === 'addToGroup' && <AddToGroupDialog person={person} onClose={() => setDialog(null)} />}
      {dialog?.kind === 'leaveGroup' && (
        <RemoveMemberDialog group={dialog.group} person={person} onClose={() => setDialog(null)} />
      )}
      {dialog?.kind === 'stopManaging' && (
        <RemoveManagerDialog group={dialog.group} person={person} onClose={() => setDialog(null)} />
      )}
    </div>
  )
}
